import React, { useState } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import PhotoUpload from "./PhotoUpload";
import { MagnifyingGlass, Minus, CheckCircle } from "@phosphor-icons/react";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function DumperRemovalDialog({ open, onOpenChange, onDone, siteId }) {
  const { meta } = useAuth();
  const [dumper, setDumper] = useState("");
  const [engineHours, setEngineHours] = useState("");
  const [performedDate, setPerformedDate] = useState(todayStr());
  const [estimated, setEstimated] = useState(false);
  const [tyres, setTyres] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState(null);
  const [pickedSize, setPickedSize] = useState(null);
  const [pickedSite, setPickedSite] = useState(null);

  const reset = () => { setDumper(""); setEngineHours(""); setPerformedDate(todayStr()); setEstimated(false); setTyres(null); setSelected(null); setDetail({}); setMatches(null); setPickedSize(null); setPickedSite(null); };

  const fetchEstimate = async (dumperNo, date, sz) => {
    try {
      const sq = pickedSite ? `&mine_site_id=${pickedSite}` : "";
      const zq = sz ? `&size=${encodeURIComponent(sz)}` : "";
      const { data } = await api.get(`/estimate-hours?dumper_number=${encodeURIComponent(dumperNo)}&date=${date}${sq}${zq}`);
      if (data.estimated_hours != null) { setEngineHours(data.estimated_hours); setEstimated(true); }
    } catch (e) { console.debug("estimate-hours unavailable (new dumper?)", e); }
  };

  const loadTyres = async (sizeArg, siteArg) => {
    if (!dumper.trim()) { toast.error("Enter a dumper number"); return; }
    const sz = sizeArg;
    const st = siteArg;
    setLoading(true);
    try {
      const params = new URLSearchParams({ dumper_number: dumper.trim() });
      if (st) params.set("mine_site_id", st);
      if (sz) params.set("size", sz);
      const { data } = await api.get(`/dumper-tyres?${params.toString()}`);
      if (data.ambiguous) { setMatches(data.matches); setTyres(null); setLoading(false); return; }
      setMatches(null); setPickedSize(data.size); setPickedSite(data.mine_site_id);
      setTyres(data.tyres);
      setSelected(null);
      setDetail({});
      await fetchEstimate(dumper.trim(), performedDate, data.size);
      if (data.tyres.length === 0) toast.info("No fitted tyres on this dumper");
    } catch (e) { toast.error(`Could not load: ${e.response?.data?.detail || e.message || "network error"}`); setTyres(null); setMatches(null); }
    finally { setLoading(false); }
  };

  const onDateChange = (d) => {
    setPerformedDate(d);
    if (tyres && dumper.trim() && (estimated || engineHours === "")) fetchEstimate(dumper.trim(), d, pickedSize);
  };

  const set = (k, v) => setDetail((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    if (!selected) { toast.error("Select the tyre to remove"); return; }
    if (!detail.reason) { toast.error("Reason for removal is required"); return; }
    setSaving(true);
    try {
      await api.post("/removals", {
        tyre_id: selected,
        engine_hours: engineHours === "" ? undefined : Number(engineHours),
        performed_date: performedDate,
        reason: detail.reason,
        vehicle_km: detail.vehicle_km === "" || detail.vehicle_km == null ? undefined : Number(detail.vehicle_km),
        km_override: detail.km_override || undefined,
        condition: detail.condition || undefined,
        tread_depth: detail.tread_depth === "" || detail.tread_depth == null ? undefined : Number(detail.tread_depth),
        remarks: detail.remarks || undefined,
        photos: (detail.photos || []).map((p) => ({ id: p.id, url: p.url })),
      });
      const sel = tyres.find((t) => t.tyre_id === selected);
      toast.success(`Removed ${sel?.serial_number || "tyre"} from ${dumper.trim()}`);
      onOpenChange(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove tyre"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="dialog-remove-dumper">
        <DialogHeader><DialogTitle className="font-display">Remove Tyre</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label className="text-xs text-zinc-400">Dumper Number *</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="field-dumper_number" value={dumper} onChange={(e) => setDumper(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadTyres()} placeholder="e.g. HAUL-77" className="bg-zinc-950" />
              <Button data-testid="load-dumper-btn" onClick={() => loadTyres()} disabled={loading} variant="outline">
                <MagnifyingGlass size={16} className="mr-1" /> {loading ? "…" : "Load"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Date it happened</Label>
            <Input data-testid="field-performed_date" type="date" value={performedDate}
              onChange={(e) => onDateChange(e.target.value)} className="mt-1 bg-zinc-950" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Engine Hrs at Removal</Label>
            <Input data-testid="field-engine_hours" type="number" value={engineHours}
              onChange={(e) => { setEstimated(false); setEngineHours(e.target.value === "" ? "" : Number(e.target.value)); }} className="mt-1 bg-zinc-950" />
            {estimated && <span className="mt-1 block text-[10px] text-amber-500">≈ estimated from date — edit if known</span>}
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Current Vehicle KM</Label>
            <Input data-testid="field-vehicle_km" type="number" value={detail.vehicle_km ?? ""}
              onChange={(e) => setDetail({ ...detail, vehicle_km: e.target.value })} className="mt-1 bg-zinc-950" />
          </div>
        </div>

        {matches && (
          <div className="mt-2 rounded-sm border border-amber-500/40 bg-amber-500/5 p-3" data-testid="dumper-size-picker">
            <div className="mb-2 text-xs text-amber-400">Two dumpers are numbered "{dumper.trim()}" here — pick the tyre size:</div>
            <div className="flex flex-wrap gap-2">
              {matches.map((m) => (
                <Button key={m.size} variant="outline" data-testid={`pick-size-${m.size}`}
                  onClick={() => loadTyres(m.size, m.mine_site_id)}>{m.site_name ? m.site_name + " · " : ""}{m.size}</Button>
              ))}
            </div>
          </div>
        )}

        {tyres && tyres.length > 0 && (
          <div className="mt-2">
            <div className="mb-2 overline text-zinc-500">{tyres.length} fitted tyres — select the one to remove</div>
            <div className="space-y-2">
              {tyres.map((t) => {
                const isSel = selected === t.tyre_id;
                return (
                  <div key={t.tyre_id} data-testid={`remove-tyre-${t.tyre_id}`}
                    className={`rounded-sm border p-3 transition-colors ${isSel ? "border-rose-500/50 bg-rose-500/5" : "border-zinc-800 bg-zinc-950"}`}>
                    <button type="button" data-testid={`select-remove-${t.tyre_id}`}
                      onClick={() => { setSelected(isSel ? null : t.tyre_id); }}
                      className="flex w-full items-center justify-between text-left">
                      <div>
                        <div className="text-sm font-semibold">{t.position}</div>
                        <div className="font-mono text-xs text-amber-500">{t.serial_number}
                          <span className="ml-2 text-zinc-500">{t.current_tread_depth != null ? `${t.current_tread_depth}mm` : ""}</span>
                        </div>
                      </div>
                      <span className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs font-semibold ${
                        isSel ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-zinc-700 text-zinc-400"}`}>
                        {isSel ? <CheckCircle size={14} weight="bold" /> : <Minus size={14} weight="bold" />}
                        {isSel ? "Selected" : "Select"}
                      </span>
                    </button>

                    {isSel && (
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <Label className="text-xs text-zinc-400">Reason for Removal *</Label>
                          <Select value={detail.reason || ""} onValueChange={(v) => set("reason", v)}>
                            <SelectTrigger data-testid="remove-reason" className="mt-1 bg-zinc-900"><SelectValue placeholder="Select reason" /></SelectTrigger>
                            <SelectContent>{(meta?.removal_reasons || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-400">Tyre Condition at Removal</Label>
                          <Select value={detail.condition || ""} onValueChange={(v) => set("condition", v)}>
                            <SelectTrigger data-testid="remove-condition" className="mt-1 bg-zinc-900"><SelectValue placeholder="Condition" /></SelectTrigger>
                            <SelectContent>{(meta?.conditions || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-400">Tread Depth (mm)</Label>
                          <Input type="number" data-testid="remove-tread" value={detail.tread_depth ?? ""}
                            onChange={(e) => set("tread_depth", e.target.value)} className="mt-1 bg-zinc-900" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs text-zinc-400">Remarks</Label>
                          <Textarea data-testid="remove-remarks" value={detail.remarks || ""}
                            onChange={(e) => set("remarks", e.target.value)} className="mt-1 bg-zinc-900" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs text-zinc-400">Photos</Label>
                          <div className="mt-1">
                            <PhotoUpload testid="remove-photos" value={detail.photos || []} onChange={(v) => set("photos", v)} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !selected} data-testid="dumper-remove-submit"
            className="bg-rose-500 font-bold text-zinc-950 hover:bg-rose-400">
            {saving ? "Removing..." : "Remove Tyre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
